/**
 * Landschafts-Stufen - EINE Quelle fuer die Oberflaeche.
 *
 * Vier Stufen in fester Reihenfolge (David 31.07.2026): DEV, QAS, PRE-PROD, PROD. Ein Space darf
 * zwei, drei oder alle vier nutzen. Transportiert wird immer vorwaerts, und eine Stufe, die der
 * Space nicht hat, wird uebersprungen statt zu blockieren.
 *
 * ⛔ Nie auf die Ordnungszahl vergleichen. Bis zum 31.07.2026 bedeutete `stageOrder === 2` PROD;
 * seit PRE-PROD dazwischen liegt, ist 2 die Vorstufe und 3 die Produktion. Jeder Vergleich auf
 * die nackte Zahl kippt damit lautlos auf die falsche Landschaft. `order` ist ausschliesslich
 * zum Sortieren und fuer die Vorwaerts-Pruefung da - Bedeutung tragen die Namen.
 *
 * Die Werte spiegeln STAGES in backend/server.js. Das Backend liefert `stage` und `stageOrder`
 * in jeder Instanz-Antwort mit; hier stehen nur Beschriftung und die Regeln darueber.
 */

export const STAGES = [
  { key: "dev", order: 0, label: "DEV", env: "DEV" },
  { key: "qas", order: 1, label: "QAS", env: "QAS" },
  { key: "preprod", order: 2, label: "PRE-PROD", env: "PRE-PROD" },
  { key: "prod", order: 3, label: "PROD", env: "PROD" },
];

export const STAGE_KEYS = STAGES.map(s => s.key);

/**
 * Tag-Schreibweise -> Stufe. Spiegelt STAGE_ORDER_MAP in backend/server.js.
 * Die Aliase der Bestandsdaten bleiben gueltig: "test"/"tst"/"qa" zaehlen weiter als QAS,
 * damit kein vorhandener Tag angefasst werden muss.
 */
export const TAG_ZU_STUFE = {
  dev: "dev", develop: "dev", development: "dev",
  qas: "qas", qa: "qas", tst: "qas", test: "qas",
  preprod: "preprod", "pre-prod": "preprod", pre_prod: "preprod", pp: "preprod",
  stage: "preprod", staging: "preprod", preproduction: "preprod",
  prod: "prod", prd: "prod", production: "prod", live: "prod",
};

/** Stufe aus Tags ableiten - gleiche Regel wie im Backend. */
export function stufeAusTags(tags) {
  for (const t of (Array.isArray(tags) ? tags : [])) {
    const k = String(t || "").trim().toLowerCase();
    if (TAG_ZU_STUFE[k]) return TAG_ZU_STUFE[k];
  }
  return null;
}

/** Beschriftung einer Stufe. Unbekanntes wird gross geschrieben statt verschluckt. */
export function stageLabel(key) {
  return STAGES.find(s => s.key === key)?.label || String(key || "").toUpperCase();
}

export function stageOrderOf(key) {
  const s = STAGES.find(x => x.key === key);
  return s ? s.order : null;
}

/**
 * Produktionsnah = PROD und PRE-PROD. PRE-PROD traegt bei vielen Kunden echte Daten, deshalb
 * gelten dort dieselben Warnungen wie in PROD (Davids Entscheidung 31.07.2026).
 */
export function istProdNah(key) {
  return key === "prod" || key === "preprod";
}

/**
 * Die Stufen eines Space in Reihenfolge - nur die, die es wirklich gibt.
 * instanzen: Objekte mit `stage` (aus der API).
 */
export function stufenDesSpace(instanzen) {
  const vorhanden = new Set((instanzen || []).map(i => i && i.stage).filter(Boolean));
  return STAGES.filter(s => vorhanden.has(s.key));
}

/**
 * Naechste VORHANDENE Stufe vorwaerts. Fehlende werden uebersprungen: ein Space mit
 * DEV + PRE-PROD + PROD transportiert von DEV direkt nach PRE-PROD.
 * Gibt null zurueck, wenn es keine spaetere Stufe gibt (die Endstufe transportiert nicht weiter).
 */
export function naechsteStufe(vorhandeneKeys, vonKey) {
  const von = STAGES.find(s => s.key === vonKey);
  if (!von) return null;
  const treffer = STAGES
    .filter(s => s.order > von.order && (vorhandeneKeys || []).includes(s.key))
    .sort((a, b) => a.order - b.order);
  return treffer.length ? treffer[0].key : null;
}

/**
 * Die letzte Stufe vor PROD, die der Space wirklich hat - die Sammelstufe fuer den
 * Release-Kandidaten. Bei DEV/QAS/PRE-PROD/PROD ist das PRE-PROD, bei DEV/QAS/PROD das QAS.
 * Ohne PROD oder ohne Vorstufe null.
 */
export function letzteStufeVorProd(vorhandeneKeys) {
  const vorhanden = vorhandeneKeys || [];
  if (!vorhanden.includes("prod")) return null;
  const prodOrder = stageOrderOf("prod");
  const davor = STAGES
    .filter(s => s.order < prodOrder && vorhanden.includes(s.key))
    .sort((a, b) => b.order - a.order);
  return davor.length ? davor[0].key : null;
}
